import type { OrderAddressDTO, OrderShippingAdditionalDataDTO } from 'ecosystem'
import { isNullable } from 'shared-utils'
import type { BuiltShipment } from './buildShipmentFromPostnord'

type NshiftDetails = OrderShippingAdditionalDataDTO['nshiftDetails']

/**
 * Map an Nshift booking/checkout result to the canonical `ShipmentDTO`.
 *
 * `shipmentId` is set to `bookingDetails.bookingOrderId` once the shopper has
 * picked a carrier; on the initial DRAFT (no widgetResult yet) only the
 * provider stays populated.
 */
export const buildShipmentFromNshift = (
  nshiftResponse?: NshiftDetails
): BuiltShipment | undefined => {
  if (!nshiftResponse) return undefined

  if (nshiftResponse.isPickupFromStore) {
    return {
      shippingCost: 0,
      shipment: {
        shippingProvider: 'NSHIFT',
        shippingType: 'PICKUP',
        additionalInfo: {
          nshiftDetails: { isPickupFromStore: true }
        }
      }
    }
  }

  const recipientAddress: OrderAddressDTO | undefined = nshiftResponse.recipientAddress
    ? {
        address1: nshiftResponse.recipientAddress.address1 ?? '',
        address2: nshiftResponse.recipientAddress.address2 ?? '',
        country: nshiftResponse.recipientAddress.country,
        mobilePhone:
          nshiftResponse.recipientAddress.mobile || nshiftResponse.recipientAddress.phone || '',
        postalCode: nshiftResponse.recipientAddress.postalCode,
        postalRegion: nshiftResponse.recipientAddress.city || '',
        attention: '',
        reference:
          `Nshift ${nshiftResponse.bookingDetails?.carrier || ''} ${nshiftResponse.bookingDetails?.carrierProduct || ''}`.trim()
      }
    : undefined

  const pickupAddress: OrderAddressDTO | undefined = nshiftResponse.bookingDetails?.pickupPoint
    ? {
        address1: nshiftResponse.bookingDetails.pickupPoint.address1 ?? '',
        address2: nshiftResponse.bookingDetails.pickupPoint.address2 ?? '',
        country: nshiftResponse.bookingDetails.pickupPoint.countryCode,
        mobilePhone:
          nshiftResponse.bookingDetails.pickupPoint.mobile ||
          nshiftResponse.bookingDetails.pickupPoint.phone ||
          '',
        postalCode: nshiftResponse.bookingDetails.pickupPoint.postalCode,
        postalRegion: nshiftResponse.bookingDetails.pickupPoint.city || '',
        attention: '',
        reference:
          `Nshift ${nshiftResponse.bookingDetails.carrier || ''} ${nshiftResponse.bookingDetails.carrierProduct || ''}`.trim()
      }
    : undefined

  const address = pickupAddress ?? recipientAddress

  const carrierName = nshiftResponse.bookingDetails?.carrier
  const carrierService = nshiftResponse.bookingDetails?.carrierProduct
  const locationId = nshiftResponse.bookingDetails?.pickupPoint?.id
  const locationName = nshiftResponse.bookingDetails?.pickupPoint?.name
  const shippingCost = nshiftResponse.widgetResult?.totalPrice
  const shipmentId = nshiftResponse.bookingDetails?.bookingOrderId

  return {
    ...(isNullable(shippingCost) ? {} : { shippingCost }),
    shipment: {
      shippingProvider: 'NSHIFT',
      shippingType: 'DELIVERY',
      ...(shipmentId ? { shipmentId } : {}),
      ...(carrierName ? { carrierName } : {}),
      ...(carrierService ? { carrierService } : {}),
      ...(address ? { address } : {}),
      ...(locationId ? { locationId } : {}),
      ...(locationName ? { locationName } : {}),
      additionalInfo: { nshiftDetails: nshiftResponse }
    }
  }
}
